import { useLayoutEffect, useRef } from "react";
import type { UseFormReturn } from "react-hook-form";
import { applyScale, calculateScaleFromSize } from "@design-lab/entities/artwork/lib/artworkScale";
import { getSideFromPlacement } from "@design-lab/shared/lib/artworkUtils";
import type { PlacementFormPosition } from "@design-lab/shared/types";
import type { Surface } from "@design-lab/features/canvas/model/types";
import type { ArtworkDimensions } from "@design-lab/shared/types/artwork";

type AutoSizingParams = {
  form: UseFormReturn<any>;
  placement: PlacementFormPosition;
  surface: Surface | undefined;
  artworkImage: HTMLImageElement | undefined;
  artworkSizeBounds: ArtworkDimensions | undefined;
};

/**
 * Hook to automatically size and scale artwork based on placement and image changes
 * Extracted from original Artboard component
 */
export function useDesignLabArtworkAutoSizing({
  form,
  placement,
  surface,
  artworkImage,
  artworkSizeBounds
}: AutoSizingParams) {
  const prevValuesRef = useRef({
    surface,
    artworkImage,
    placement
  });

  useLayoutEffect(() => {
    if (!artworkSizeBounds || artworkSizeBounds.maxWidthInches === 0) {
      return;
    }

    const prev = prevValuesRef.current;
    const artworkImageChanged = prev.artworkImage !== artworkImage;
    const placementChanged = prev.placement !== placement;

    prevValuesRef.current = {
      surface,
      artworkImage,
      placement
    };

    const currentSize = form.getValues(`${placement}.size`);
    const infoPath = getSideFromPlacement(placement);
    const currentScale = form.getValues(`info.${infoPath}.scale`);
    const { minWidthInches, minHeightInches, maxWidthInches, maxHeightInches } = artworkSizeBounds;

    const sizeValue = currentSize as { width: number; height: number } | null | undefined;
    if (!sizeValue || sizeValue.width === 0 || sizeValue.height === 0) {
      const newSize = {
        width: maxWidthInches,
        height: maxHeightInches
      };

      form.setValue(`${placement}.size`, newSize);
      form.setValue(`info.${infoPath}.scale`, 1.0);
      return;
    }

    if (currentScale && (artworkImageChanged || placementChanged)) {
      const newSize = applyScale(currentScale, artworkSizeBounds);

      if (newSize.width !== sizeValue.width || newSize.height !== sizeValue.height) {
        form.setValue(`${placement}.size`, newSize);
      }
      return;
    }

    const { width, height } = sizeValue;
    const currentAspectRatio = Number((width / height).toFixed(2));

    let finalSize: { width: number; height: number };

    if (currentAspectRatio !== artworkSizeBounds.maxAspectRatioByWidth) {
      const r = artworkSizeBounds.maxAspectRatioByWidth;
      if (r > 1) {
        // Wide image: derive height from width. If height hits a bound, re-derive
        // width from the clamped height so aspect ratio is always preserved (PIP-3131 Class B).
        const derivedHeight = width / r;
        const clampedHeight = Math.max(minHeightInches, Math.min(derivedHeight, maxHeightInches));
        const finalWidth = clampedHeight !== derivedHeight ? clampedHeight * r : width;
        finalSize = {
          width: Math.max(minWidthInches, Math.min(finalWidth, maxWidthInches)),
          height: clampedHeight
        };
      } else {
        // Tall image: derive width from height. If width hits a bound, re-derive
        // height from the clamped width so aspect ratio is always preserved (PIP-3131 Class B).
        const derivedWidth = height * r;
        const clampedWidth = Math.max(minWidthInches, Math.min(derivedWidth, maxWidthInches));
        const finalHeight = clampedWidth !== derivedWidth ? clampedWidth / r : height;
        finalSize = {
          width: clampedWidth,
          height: Math.max(minHeightInches, Math.min(finalHeight, maxHeightInches))
        };
      }
    } else {
      finalSize = {
        width: Math.max(minWidthInches, Math.min(width, maxWidthInches)),
        height: Math.max(minHeightInches, Math.min(height, maxHeightInches))
      };
    }

    if (finalSize.width !== width || finalSize.height !== height) {
      form.setValue(`${placement}.size`, finalSize);
    }

    const scale = calculateScaleFromSize(finalSize, artworkSizeBounds);
    form.setValue(`info.${infoPath}.scale`, scale);
  }, [artworkImage, artworkSizeBounds, placement, surface, form]);
}
